import { useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../shared/api.js';
import { formatIQD } from '../../../shared/format.js';
import { PackageCard } from '../components/PackageCard.jsx';
import { useReceptionForm } from '../hooks/useReceptionForm.js';
import { useHiddenPricingTypes } from '../../../shared/useSettings.js';
import { useSubscriptionCoverage } from '../hooks/useSubscriptionCoverage.js';
import { formatDate } from '../../../shared/format.js';

const PACKAGE_ICONS = ['🕐', '🕑', '🕒', '🕓', '🎉', '🎂'];

// أنواع التسعير الأربعة الممكنة. ما يظهر منها في الاستقبال يُحدَّد ديناميكياً
// من خلال إعداد `hiddenPricingTypes` في الإعدادات العامة.
const TYPES = [
  { key: 'package', label: 'الباقات',   icon: '🎉', sub: 'باقات بمدّة محددة' },
  { key: 'hourly',  label: 'ساعي',      icon: '⏱️', sub: 'حسب مدة البقاء' },
  { key: 'fixed',   label: 'مبلغ ثابت', icon: '🎫', sub: 'دخول بمبلغ مقطوع' },
  { key: 'open',    label: 'وقت مفتوح', icon: '♾️', sub: 'مبلغ مفتوح بدون وقت' }
];

export function Step3Package() {
  const pricing = useReceptionForm((s) => s.pricing);
  const setPricing = useReceptionForm((s) => s.setPricing);
  const children = useReceptionForm((s) => s.children);
  const hidden = useHiddenPricingTypes();
  // المشترك شهرياً لا يُحاسَب على اللعب مهما كان نوع التسعير المختار.
  const coverage = useSubscriptionCoverage();
  const coveredChildren = children.filter(coverage.isCovered);

  const availableTypes = useMemo(
    () => TYPES.filter((t) => !hidden.includes(t.key)),
    [hidden]
  );

  const packagesEnabled = availableTypes.some((t) => t.key === 'package');
  const hourlyEnabled   = availableTypes.some((t) => t.key === 'hourly');

  const packagesQuery = useQuery({
    queryKey: ['pricing', 'packages', { active: true }],
    queryFn: async () => {
      const { data } = await api.get('/pricing/packages', { params: { active: true } });
      return data?.items || data?.packages || data?.data || (Array.isArray(data) ? data : []);
    },
    enabled: packagesEnabled
  });

  const rulesQuery = useQuery({
    queryKey: ['pricing', 'rules'],
    queryFn: async () => {
      const { data } = await api.get('/pricing/rules');
      return data?.rules || data?.data || (Array.isArray(data) ? data : []);
    },
    enabled: hourlyEnabled
  });

  const packages = packagesQuery.data || [];
  const rules = rulesQuery.data || [];

  // إذا كان نوع التسعير الحالي مُخفى من الإعدادات، انتقل تلقائياً إلى أول
  // نوع متاح بدلاً من ترك المستخدم في حالة غير صالحة.
  useEffect(() => {
    if (!availableTypes.length) return;
    if (!availableTypes.some((t) => t.key === pricing.type)) {
      setPricing({
        type: availableTypes[0].key,
        packageId: null,
        package: null,
        ruleId: null,
        rule: null,
        fixedAmount: 0,
        openAmount: 0,
        estimatedAmount: 0
      });
    }
  }, [availableTypes, pricing.type]); // eslint-disable-line react-hooks/exhaustive-deps

  // اختيار أول باقة افتراضياً
  useEffect(() => {
    if (pricing.type !== 'package' || !packages.length || pricing.packageId) return;
    const first = packages[0];
    setPricing({ type: 'package', packageId: first._id || first.id, package: first });
  }, [packages, pricing.type]); // eslint-disable-line react-hooks/exhaustive-deps

  // اختيار القاعدة الافتراضية للتسعير الساعي
  useEffect(() => {
    if (pricing.type !== 'hourly' || !rules.length || pricing.ruleId) return;
    const def = rules.find((r) => r.isDefault) || rules[0];
    setPricing({ type: 'hourly', ruleId: def._id || def.id, rule: def });
  }, [rules, pricing.type]); // eslint-disable-line react-hooks/exhaustive-deps

  // تقدير المبلغ حسب النوع الحالي
  useEffect(() => {
    let cancelled = false;
    async function simulate() {
      try {
        const start = new Date();
        const body = {
          startTime: start.toISOString(),
          endTime: start.toISOString(),
          pricingType: pricing.type,
          siblingsIndex: 0
        };
        if (pricing.type === 'hourly')  body.ruleId      = pricing.ruleId || undefined;
        if (pricing.type === 'package') body.packageId   = pricing.packageId || undefined;
        if (pricing.type === 'fixed')   body.fixedAmount = Number(pricing.fixedAmount) || 0;
        if (pricing.type === 'open')    body.openAmount  = Number(pricing.openAmount) || 0;
        const { data } = await api.post('/pricing/simulate', body);
        if (cancelled) return;
        const total =
          data?.total ?? data?.amount ?? data?.estimatedAmount ?? data?.data?.total ?? 0;
        setPricing({ estimatedAmount: Number(total) || 0 });
      } catch {
        // silent — preview only
      }
    }
    const ready =
      (pricing.type === 'package' && pricing.packageId) ||
      (pricing.type === 'hourly'  && pricing.ruleId) ||
      (pricing.type === 'fixed'   && Number(pricing.fixedAmount) > 0) ||
      (pricing.type === 'open'    && Number(pricing.openAmount) > 0);
    if (ready) simulate();
    return () => { cancelled = true; };
  }, [
    pricing.type,
    pricing.packageId,
    pricing.ruleId,
    pricing.fixedAmount,
    pricing.openAmount,
    children.length
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  function switchType(key) {
    if (key === pricing.type) return;
    setPricing({
      type: key,
      packageId: key === 'package' ? pricing.packageId : null,
      package:   key === 'package' ? pricing.package   : null,
      ruleId:    key === 'hourly'  ? pricing.ruleId    : null,
      rule:      key === 'hourly'  ? pricing.rule      : null,
      fixedAmount: key === 'fixed' ? pricing.fixedAmount : 0,
      openAmount:  key === 'open'  ? pricing.openAmount  : 0,
      estimatedAmount: 0
    });
  }

  // كل الأطفال مشتركون => لا مبلغ تقديري أصلاً.
  const allCovered = children.length > 0 && coveredChildren.length === children.length;
  const showEstimate = !allCovered && (
    (pricing.type === 'package' && pricing.packageId) ||
    (pricing.type === 'fixed'   && Number(pricing.fixedAmount) > 0) ||
    (pricing.type === 'open'    && Number(pricing.openAmount)  > 0));

  return (
    <div>
      <div className="card-h">
        <div className="flex-1">
          <h3 className="text-[14.5px] font-bold">نوع الدخول والتسعير</h3>
          <div className="text-xs text-ink-3 mt-0.5">
            {availableTypes.length > 1
              ? 'اختر نوع التسعير ثم حدّد التفاصيل'
              : 'حدّد تفاصيل التسعير'}
          </div>
        </div>
      </div>
      <div className="card-b space-y-4">
        {coveredChildren.length ? (
          <div
            className="rounded-[10px] px-3 py-2.5 text-[12.5px] space-y-1"
            style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534' }}
          >
            <div className="font-bold">
              {coveredChildren.length === children.length
                ? 'اشتراك شهري فعّال — لا رسوم لعب'
                : `${coveredChildren.length} من الأطفال عليهم اشتراك شهري فعّال`}
            </div>
            {coveredChildren.map((c) => {
              const sub = coverage.byChildId.get(String(c.childId));
              return (
                <div key={c.tempId} className="text-[11.5px]">
                  {c.name || 'طفل'} — {sub?.subscription?.planName || 'اشتراك شهري'} حتى{' '}
                  {formatDate(sub?.subscription?.endDate)}
                </div>
              );
            })}
            <div className="text-[11.5px]">
              يدخل بلا رسوم ولا وقت محتسب. الكافتيريا والألعاب والمرافقون تُدفع كالمعتاد.
            </div>
          </div>
        ) : null}

        {availableTypes.length === 0 ? (
          <div className="text-center text-ink-3 py-8 text-[13px] bg-paper-2 rounded-[10px]">
            جميع أنواع التسعير مُعطّلة من الإعدادات العامة. فعّل نوعاً واحداً على الأقل لمتابعة الاستقبال.
          </div>
        ) : (
          <>
            {availableTypes.length > 1 ? (
              <div
                className="grid gap-2"
                style={{ gridTemplateColumns: `repeat(${availableTypes.length}, minmax(0, 1fr))` }}
              >
                {availableTypes.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => switchType(t.key)}
                    className={clsx(
                      'flex flex-col items-center gap-0.5 px-2 py-2.5 rounded-[10px] border transition-colors',
                      pricing.type === t.key
                        ? 'bg-orange text-white border-orange shadow-orange-glow'
                        : 'bg-white text-ink-2 border-line hover:bg-paper-2'
                    )}
                  >
                    <span className="text-[18px]">{t.icon}</span>
                    <span className="text-[12.5px] font-semibold">{t.label}</span>
                    <span
                      className={clsx(
                        'text-[10.5px]',
                        pricing.type === t.key ? 'text-white/80' : 'text-ink-3'
                      )}
                    >
                      {t.sub}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}

            {pricing.type === 'package' ? (
              packagesQuery.isLoading ? (
                <div className="text-center text-ink-3 py-8 text-[13px]">جاري تحميل الباقات...</div>
              ) : packages.length === 0 ? (
                <div className="text-center text-ink-3 py-8 text-[13px] bg-paper-2 rounded-[10px]">
                  لا توجد باقات نشطة — أنشئ باقة من شاشة التسعير أولاً.
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2.5">
                  {packages.map((p, i) => (
                    <PackageCard
                      key={p._id || p.id}
                      icon={PACKAGE_ICONS[i % PACKAGE_ICONS.length]}
                      name={p.name}
                      price={p.price}
                      sub={`${p.durationMinutes} دقيقة • إضافي ${formatIQD(p.extraHourPrice || 0)}/ساعة`}
                      selected={pricing.packageId === (p._id || p.id)}
                      onClick={() =>
                        setPricing({
                          type: 'package',
                          packageId: p._id || p.id,
                          package: p
                        })
                      }
                    />
                  ))}
                </div>
              )
            ) : null}

            {pricing.type === 'hourly' ? (
              rulesQuery.isLoading ? (
                <div className="text-center text-ink-3 py-8 text-[13px]">جاري تحميل القواعد...</div>
              ) : rules.length === 0 ? (
                <div className="text-center text-ink-3 py-8 text-[13px] bg-paper-2 rounded-[10px]">
                  لا توجد قواعد ساعية — أنشئ قاعدة من شاشة التسعير أولاً.
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-ink-2 mb-1">
                    قاعدة التسعير الساعي
                  </label>
                  <select
                    className="input"
                    value={pricing.ruleId || ''}
                    onChange={(e) => {
                      const id = e.target.value;
                      const rule =
                        rules.find((r) => String(r._id || r.id) === String(id)) || null;
                      setPricing({ type: 'hourly', ruleId: id || null, rule });
                    }}
                  >
                    <option value="">— اختر قاعدة —</option>
                    {rules.map((r) => (
                      <option key={r._id || r.id} value={r._id || r.id}>
                        {r.name}
                        {r.isDefault ? ' (افتراضية)' : ''}
                        {r.pricePerHour ? ` — ${formatIQD(r.pricePerHour)}/ساعة` : ''}
                      </option>
                    ))}
                  </select>
                  <div className="text-[11.5px] text-ink-3">
                    التسعير الساعي يُحتسب فعلياً عند الخروج حسب وقت اللعب.
                  </div>
                </div>
              )
            ) : null}

            {pricing.type === 'fixed' ? (
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">
                  المبلغ الثابت (د.ع)
                </label>
                <input
                  className="input"
                  type="number"
                  min="0"
                  value={pricing.fixedAmount || ''}
                  onChange={(e) =>
                    setPricing({ type: 'fixed', fixedAmount: Number(e.target.value) || 0 })
                  }
                  placeholder="0"
                />
                <div className="text-[11.5px] text-ink-3 mt-1">
                  مبلغ مقطوع للدخول، يُطبَّق على كل طفل.
                </div>
              </div>
            ) : null}

            {pricing.type === 'open' ? (
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">
                  المبلغ المفتوح (د.ع)
                </label>
                <input
                  className="input"
                  type="number"
                  min="0"
                  value={pricing.openAmount || ''}
                  onChange={(e) =>
                    setPricing({ type: 'open', openAmount: Number(e.target.value) || 0 })
                  }
                  placeholder="0"
                />
                <div className="text-[11.5px] text-ink-3 mt-1">
                  مبلغ بدون قيد زمني، يُطبَّق على كل طفل.
                </div>
              </div>
            ) : null}

            {showEstimate ? (
              <div className="p-3 rounded-[10px] bg-orange-soft text-orange-deep flex items-center justify-between">
                <div className="text-[12.5px]">
                  المبلغ التقريبي للطفل الواحد:
                  {children.length > 1 ? (
                    <span className="text-[11px] block">
                      قد يُطبَّق خصم الأشقاء عند فتح الجلسات.
                    </span>
                  ) : null}
                </div>
                <div className="text-[18px] font-extrabold">
                  {formatIQD(pricing.estimatedAmount || 0)}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
